import camelize from 'camelize';

type RawRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is RawRecord =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const stringifyConfig = (config: unknown): unknown => {
  if (!isRecord(config)) {
    return config;
  }
  try {
    return JSON.stringify(config);
  } catch {
    return config;
  }
};

/** Returns `message` with `data_json.agent.nodes[].config` objects turned into JSON strings, or
 * `message` itself when it carries no agent builder draft. */
const normalizeMessage = (message: unknown): unknown => {
  if (!isRecord(message) || !isRecord(message.data_json)) {
    return message;
  }
  const dataJson = message.data_json;
  const { agent } = dataJson;
  if (!isRecord(agent) || !Array.isArray(agent.nodes)) {
    return message;
  }
  return {
    ...message,
    data_json: {
      ...dataJson,
      agent: {
        ...agent,
        nodes: agent.nodes.map(node =>
          isRecord(node)
            ? { ...node, config: stringifyConfig(node.config) }
            : node
        )
      }
    }
  };
};

const normalizeEnvelope = (envelope: unknown): unknown => {
  if (!isRecord(envelope)) {
    return envelope;
  }
  let next = normalizeMessage(envelope) as RawRecord;
  if (isRecord(next.data)) {
    next = { ...next, data: normalizeMessage(next.data) };
  }
  if (Array.isArray(next.messages)) {
    next = { ...next, messages: next.messages.map(normalizeMessage) };
  }
  return next;
};

/**
 * Agent builder chats (`feature: 1`) send each draft node's `config` as a plain object in
 * `data_json.agent` (BE guide AGENT_BUILDER_CHATS_FE_guide.md, 2026-10). Copilot payloads are
 * deep-camelized before they reach Redux, which would rewrite config keys (`filter_groups` →
 * `filterGroups`) and corrupt uuid-keyed `fields` dicts (see `restoreRawFieldsDictByUuid` in
 * `services/agents.ts`). Stringifying each config on the raw payload first keeps it out of
 * `camelize`'s reach, so the Redux draft keeps `IAgentBuilderDraftNode.config` as a JSON string
 * and `components/Agents/agentBuilderDraft.ts` parses it as before. Legacy string configs pass
 * through untouched.
 *
 * Covers the shapes Copilot messages arrive in: a stream frame (`data` is the message), a chat
 * detail (`messages[]`), a bare message, or an array of any of those.
 */
export const normalizeCopilotAgentBuilderDraftConfigs = (
  raw: unknown
): unknown =>
  Array.isArray(raw) ? raw.map(normalizeEnvelope) : normalizeEnvelope(raw);

/** `camelize` for Copilot payloads that may carry an agent builder draft — see
 * {@link normalizeCopilotAgentBuilderDraftConfigs}. */
export const camelizeCopilotPayload = <T = unknown>(raw: unknown): T =>
  camelize(normalizeCopilotAgentBuilderDraftConfigs(raw) as object) as T;
